import { Fingerprint, Lock, Monitor, ShieldAlert } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { AccountHold, SavingsAccount, SavingsProduct } from '@/api/savings-types'
import { AuditTrailDrawer } from '@/components/audit-trail-drawer'
import { MoneyDisplay } from '@/components/money-display'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatDateTime, formatShortDate } from '@/lib/datetime'
import { useIsAtLeast } from '@/lib/use-viewport'
import { cn } from '@/lib/utils'

import { holdReasonLabel, interestFrequencyLabel, productClassLabel } from './constants'
import { useSavingsPermissions } from './permissions'

/**
 * The panels behind the savings account tabs.
 *
 * The rule the customer panels established holds here too: a value the server did not send is not
 * rendered at all, and a value it sent as null renders as a dash. Withheld from this role and never
 * recorded are different facts, and a screen that showed them identically would teach an operator
 * to read one as the other.
 *
 * The balance panel is where the care goes. Three figures that differ by a hold are three different
 * claims, and the one a teller acts on — available — is the smallest of them.
 */

function Field({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  const empty = value === null || value === undefined || value === ''

  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className={cn('mt-0.5 text-sm break-words text-heading', mono && 'font-mono')}>
        {empty ? <span className="text-muted">—</span> : value}
      </dd>
    </div>
  )
}

function Section({
  title,
  description,
  action,
  children,
}: {
  title: string
  description?: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
        {action}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

/**
 * The three balances, and what separates them.
 *
 * `available = ledger − holds`, and it is the only one a withdrawal may draw on. On a security
 * deposit account it is not withdrawable by anybody at all, which is stated rather than left to be
 * discovered when the server refuses the posting.
 *
 * Accrued interest sits apart from the three deliberately: it is not in the ledger balance and
 * does not become the customer's until an interest run capitalises it.
 */
export function BalanceSummary({ account }: { account: SavingsAccount }) {
  const held = account.holdAmountMinor > 0

  return (
    <div className="space-y-2">
      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="space-y-1">
            <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Ledger balance</p>
            <MoneyDisplay
              value={account.ledgerBalanceMinor}
              currency={account.currencyCode}
              emphasis="strong"
              className="text-left text-base"
            />
            <p className="text-2xs text-muted">What the general ledger carries.</p>
          </CardContent>
        </Card>

        <Card className={cn(held && 'border-warning-border')}>
          <CardContent className="space-y-1">
            <p className="text-2xs font-semibold tracking-wide text-muted uppercase">On hold</p>
            <MoneyDisplay
              value={account.holdAmountMinor}
              currency={account.currencyCode}
              emphasis={held ? 'strong' : 'muted'}
              className="text-left text-base"
            />
            <p className="text-2xs text-muted">
              {held
                ? `${String(account.holds.filter((hold) => hold.isActive).length)} active hold(s), earmarked against an obligation.`
                : 'Nothing earmarked.'}
            </p>
          </CardContent>
        </Card>

        <Card className="border-brand-fill/40">
          <CardContent className="space-y-1">
            <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Available</p>
            <MoneyDisplay
              value={account.availableBalanceMinor}
              currency={account.currencyCode}
              emphasis="strong"
              className="text-left text-base"
            />
            <p className="text-2xs text-muted">
              {account.isWithdrawable
                ? 'Ledger balance less holds. This is what a withdrawal may draw on.'
                : 'Not withdrawable. This is collateral, not spendable balance.'}
            </p>
          </CardContent>
        </Card>
      </div>

      {!account.isWithdrawable ? (
        <Alert tone="warning">
          <Lock aria-hidden />
          <div className="space-y-1">
            <AlertTitle>A security deposit is never withdrawn from</AlertTitle>
            <AlertDescription>
              It is cash collateral under the group guarantee. It leaves this account by a
              countersigned guarantee offset, or by closing the account — a withdrawal keyed against
              it is refused with{' '}
              <span className="font-mono text-xs">savings.account_not_withdrawable</span>.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {account.accruedInterestMinor > 0 ? (
        <p className="text-xs text-muted">
          Accrued interest{' '}
          <MoneyDisplay
            value={account.accruedInterestMinor}
            currency={account.currencyCode}
            className="inline text-left"
          />{' '}
          — accrued but not capitalised, so it is not part of the ledger balance and is not the
          customer&apos;s until an interest run posts it.
        </p>
      ) : null}
    </div>
  )
}

export function AccountOverviewPanel({
  account,
  product,
}: {
  account: SavingsAccount
  /** The product this account was opened against, when the product list is readable by this role. */
  product: SavingsProduct | undefined
}) {
  const permissions = useSavingsPermissions()
  const [auditOpen, setAuditOpen] = useState(false)

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title="Account" description="Where this account lives and what it is.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Account number" value={account.accountNumber} mono />
          <Field label="Class" value={productClassLabel(account.productClass)} />
          <Field label="Status" value={<StatusBadge status={account.status} />} />
          <Field label="Branch" value={`Branch ${account.branchId}`} />
          <Field label="Opened" value={formatShortDate(account.openedDate)} />
          <Field
            label="Closed"
            value={account.closedDate === null ? null : formatShortDate(account.closedDate)}
          />
          <Field
            label="Last movement"
            value={
              account.lastTransactionDate === null ? (
                <span className="text-muted">Never moved</span>
              ) : (
                formatShortDate(account.lastTransactionDate)
              )
            }
          />
          <Field
            label="Passbook"
            value={
              account.passbookNumber === null ? (
                <span className="text-muted">No book recorded</span>
              ) : (
                <span className="font-mono">{account.passbookNumber}</span>
              )
            }
          />
        </dl>
      </Section>

      <Section title="Customer" description="The person the money belongs to.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Name" value={account.customerName} />
          <Field label="Customer number" value={account.customerNumber} mono />
        </dl>
      </Section>

      <Section
        title="Product"
        description={
          permissions.canViewProducts
            ? 'The terms this account was opened under.'
            : 'Reading the product terms needs savings.product.view.'
        }
      >
        {product ? (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Field label="Code" value={product.code} mono />
            <Field label="Name" value={product.name} />
            <Field
              label="Interest rate"
              value={<InterestRate fraction={product.interestRatePerAnnum} />}
            />
            <Field
              label="Posting frequency"
              value={interestFrequencyLabel(product.interestPostingFrequency)}
            />
            <Field
              label="Minimum balance"
              value={
                <MoneyDisplay
                  value={product.minBalanceMinor}
                  currency={product.currencyCode}
                  className="text-left"
                />
              }
            />
            <Field
              label="Withdrawal fee"
              value={
                product.withdrawalFeeMinor === null ? (
                  <span className="text-muted">No fee</span>
                ) : (
                  <MoneyDisplay
                    value={product.withdrawalFeeMinor}
                    currency={product.currencyCode}
                    className="text-left"
                  />
                )
              }
            />
            <Field
              label="Dormancy"
              value={
                product.dormancyDays > 0
                  ? `${String(product.dormancyDays)} idle days`
                  : 'Does not age into dormancy'
              }
            />
            <Field label="Liability account" value={product.liabilityGlAccountCode} mono />
          </dl>
        ) : (
          <p className="text-sm text-muted">
            The product record is not available to this screen. The account carries its code —{' '}
            <span className="font-mono">{account.productCode}</span> — and its class, which are the
            two facts every posting rule turns on.
          </p>
        )}
      </Section>

      <Section
        title="History"
        description="Every act against this account, including the ones that were refused."
        action={
          <Button variant="secondary" size="sm" onClick={() => setAuditOpen(true)}>
            <Fingerprint />
            Audit trail
          </Button>
        }
      >
        <p className="text-sm text-body">
          The trail is append-only and sealed into a hash chain on the server. Holds are recorded
          separately, under their own entity — open a hold&apos;s trail from the Holds tab.
        </p>
      </Section>

      <AuditTrailDrawer
        open={auditOpen}
        onOpenChange={setAuditOpen}
        entityType="SavingsAccount"
        entityId={account.id}
        subject={account.accountNumber}
      />
    </div>
  )
}

/**
 * A 0–1 fraction, rendered as a percentage.
 *
 * The conversion is here rather than at the transport, and it is the only place it happens on a
 * read path. `0.075` is 7.5%, not 0.075% — printing the fraction with a `%` after it understates
 * every rate by two orders of magnitude.
 */
export function InterestRate({ fraction }: { fraction: number }) {
  if (fraction === 0) {
    return <span className="text-muted">Pays no interest</span>
  }

  // Four decimal places on the fraction is one basis point, which is the finest the rate is ever
  // set to; rounding there keeps 0.075 from printing as 7.499999999999999%.
  const percent = Math.round(fraction * 1_000_000) / 10_000

  return <span className="numeric tabular-nums">{percent}% per annum</span>
}

export function HoldsPanel({
  account,
  onPlaceHold,
  onReleaseHold,
}: {
  account: SavingsAccount
  onPlaceHold: () => void
  onReleaseHold: (hold: AccountHold) => void
}) {
  const permissions = useSavingsPermissions()
  // Placing a hold keys an amount against a real customer's account, and the mobile policy's whole
  // argument is that keying an amount is what touch is worst at. Nothing moves through a drawer, so
  // the screen itself does not refuse — but the control that opens the amount field is withheld
  // below `lg` with the reason stated, the same way the close action is. Releasing keys no amount
  // and stays available at any width: an approver stopping a lien from the road is the case the
  // control exists for.
  const isWide = useIsAtLeast('lg')
  const active = account.holds.filter((hold) => hold.isActive)
  const released = account.holds.filter((hold) => !hold.isActive)

  return (
    <div className="space-y-4">
      <Alert tone="info">
        <ShieldAlert aria-hidden />
        <div className="space-y-1">
          <AlertTitle>A hold is amended, not stacked</AlertTitle>
          <AlertDescription>
            Placing a hold whose reason already has an active hold on this account revises that hold
            in place: the amount and the note are overwritten and the original timestamp stays. There
            is no second row, and the note that was there is gone. To record a genuinely separate
            matter under a reason already in use, release the existing hold first and say why.
          </AlertDescription>
        </div>
      </Alert>

      <Section
        title="Active holds"
        description="Each one reduces the available balance by its amount."
        action={
          permissions.canPlaceHold && account.status !== 'Closed' ? (
            isWide ? (
              <Button variant="secondary" size="sm" onClick={onPlaceHold}>
                Place or amend a hold
              </Button>
            ) : (
              <p className="flex items-start gap-2 text-xs text-muted">
                <Monitor className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                Placing a hold keys an amount against this account. It is available on a wider
                display.
              </p>
            )
          ) : null
        }
      >
        {active.length === 0 ? (
          <p className="text-sm text-muted">
            Nothing is earmarked. The available balance is the whole ledger balance.
          </p>
        ) : (
          <ul className="space-y-3">
            {active.map((hold) => (
              <HoldRow
                key={hold.id}
                hold={hold}
                accountNumber={account.accountNumber}
                onRelease={permissions.canReleaseHold ? () => onReleaseHold(hold) : undefined}
              />
            ))}
          </ul>
        )}
      </Section>

      {released.length > 0 ? (
        <Section title="Released holds" description="Kept for the record. They hold nothing now.">
          <ul className="space-y-3">
            {released.map((hold) => (
              <HoldRow key={hold.id} hold={hold} accountNumber={account.accountNumber} />
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  )
}

function HoldRow({
  hold,
  accountNumber,
  onRelease,
}: {
  hold: AccountHold
  accountNumber: string
  onRelease?: () => void
}) {
  const [auditOpen, setAuditOpen] = useState(false)

  return (
    <li className="rounded-card border border-border-subtle bg-surface-muted p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={hold.isActive ? 'warning' : 'neutral'}>{holdReasonLabel(hold.reason)}</Badge>
            <MoneyDisplay
              value={hold.amountMinor}
              currency={hold.currencyCode}
              emphasis="strong"
              className="text-left"
            />
          </div>
          {/*
            The note is required by the contract, so an empty one is a note somebody left blank
            rather than a note the server withheld — and it is the only explanation a reviewer will
            have years from now.
          */}
          <p className="text-sm break-words text-body">
            {hold.note.trim() === '' ? <span className="text-muted">No note was recorded</span> : hold.note}
          </p>
          <p className="text-2xs text-muted">
            Placed {formatDateTime(hold.placedAtUtc)}
            {hold.releasedAtUtc === null ? '' : ` · released ${formatDateTime(hold.releasedAtUtc)}`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAuditOpen(true)}>
            <Fingerprint />
            Trail
          </Button>
          {onRelease ? (
            <Button variant="secondary" size="sm" onClick={onRelease}>
              Release
            </Button>
          ) : null}
        </div>
      </div>

      {/*
        Holds are audited under their own entity type with the hold's own id — not under the
        account — so who placed this, who released it, and the release reason are only reachable
        from here. None of the three is on the wire in the account's own payload.
      */}
      <AuditTrailDrawer
        open={auditOpen}
        onOpenChange={setAuditOpen}
        entityType="AccountHold"
        entityId={hold.id}
        subject={`${accountNumber} · ${holdReasonLabel(hold.reason)}`}
      />
    </li>
  )
}
